import { Component, effect, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NavTabsComponent, TabItem } from '../nav-tabs/nav-tabs.component';
import { ButtonGroupComponent, ButtonItem } from '../button-group/button-group.component';
import { OverlayDirective } from '../../directives/overlay.directive';
import { ModalService } from '../../services/modal.service';

export interface Language {
  name: string;
  code: string;
  isSelected?: boolean;
}

export interface Currency {
  name: string;
  code: string;
  symbol: string;
  isSelected?: boolean;
}

export interface Preferences {
  language?: Language;
  currency?: Currency;
}

@Component({
  standalone: true,
  imports: [
    NgTemplateOutlet,
    NavTabsComponent,
    ButtonGroupComponent,
    OverlayDirective,
  ],
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrl: './preferences.component.scss',
})
export class PreferencesComponent {
  public languages = input.required<Language[]>();
  public currencies = input.required<Currency[]>();
  public defaultPreferences = input.required<Preferences>();

  public onPreferencesChange = output<{
    action: 'CANCEL' | 'SAVE';
    preferences: Preferences;
  }>();

  public tabs = signal<TabItem[]>([
    { key: 'language', value: 'Language' },
    { key: 'currency', value: 'Currency' },
  ]);
  public activeTab = signal<string>('language');
  public activePreferences = signal<Preferences>({});
  public selectedPreferences = signal<Preferences>({});
  public actions: ButtonItem[] = [
    {
      id: 'cancel',
      label: 'Cancel',
      variant: 'secondary',
      value: 'CANCEL',
    },
    {
      id: 'save',
      label: 'Save',
      variant: 'primary',
      value: 'SAVE',
    },
  ];

  public activePreferencesEffect = effect(() => {
    this.activePreferences.set(this.defaultPreferences());
    this.selectedPreferences.set(this.defaultPreferences());
  });

  constructor(public modalSvc: ModalService) {}

  public onTabChange = (tab: string) => {
    this.activeTab.set(tab);
  };

  public updatePreference = (
    type: 'language' | 'currency',
    value: Language | Currency
  ) => {
    this.selectedPreferences.set({
      ...this.selectedPreferences(),
      [type]: value,
    });
  };

  public handleActionClick = (action: 'CANCEL' | 'SAVE') => {
    if (action === 'CANCEL') {

      this.handleModalClose(true);

    } else if (action === 'SAVE') {

      this.activePreferences.set(this.selectedPreferences());
      this.onPreferencesChange.emit({
        action: 'SAVE',
        preferences: this.activePreferences(),
      });

    }
    this.handleModalClose(false);
  };

  public handleModalClose = (isCloseClicked = true) => {
    this.activeTab.set('language');
    this.selectedPreferences.set(this.activePreferences());

    if (isCloseClicked) {
      this.onPreferencesChange.emit({
        action: 'CANCEL',
        preferences: this.selectedPreferences(),
      });
    }
    this.modalSvc.closeModal('global-modal');
  };
}